import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { FieldArray, FormikErrors, FormikProvider, useFormik } from 'formik';
import Avatar from '@mui/material/Avatar';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SaveIcon from '@mui/icons-material/Save';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PieChartIcon from '@mui/icons-material/PieChart';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { LoadingButton } from '@mui/lab';
import Alert from '@mui/material/Alert';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Tooltip from '@mui/material/Tooltip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DecimalNumberField } from '../../components/DecimalNumberField';
import { UploadFileButton } from '../../components/UploadFileButton';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { portfolioFormFields, subscribedPortfolioFormFields } from '../../forms/fields/formFields';
import { getSubscribedPortfolioFormSchema } from '../../forms/validationSchema/formSchema';
import { useAppDispatch } from '../../redux/hooks';
import { fetchAppConfigs } from '../../redux/modules/appConfig/appConfig.actions';
import { appConfigSelector } from '../../redux/modules/appConfig/appConfig.selectors';
import { fetchAssets } from '../../redux/modules/asset/asset.actions';
import { assetSelector } from '../../redux/modules/asset/asset.selectors';
import { fetchBenchmarks } from '../../redux/modules/benchmark/benchmark.actions';
import { benchmarkSelector } from '../../redux/modules/benchmark/benchmark.selectors';
import { fetchCurrencys } from '../../redux/modules/currency/currency.actions';
import { currencySelector } from '../../redux/modules/currency/currency.selectors';
import { fetchFundManagers } from '../../redux/modules/fundManager/fundManager.actions';
import { fundManagerSelector } from '../../redux/modules/fundManager/fundManager.selectors';
import { fetchGicsOptions, fetchIndexFunds } from '../../redux/modules/indexFund/indexFund.actions';
import { indexFundSelector } from '../../redux/modules/indexFund/indexFund.selectors';
import { fetchPortfolioFileTypeOptions } from '../../redux/modules/portfolio/portfolio.actions';
import { portfolioSelector } from '../../redux/modules/portfolio/portfolio.selectors';
import {
  fetchSubscribedPortfolio,
  fetchSubscribedPortfolioStatusOptions,
  resetEditSubscribedPortfolio,
  saveSubscribedPortfolio,
  updateSubscribedPortfolio,
} from '../../redux/modules/subscribedPortfolio/subscribedPortfolio.actions';
import { subscribedPortfolioSelector } from '../../redux/modules/subscribedPortfolio/subscribedPortfolio.selectors';
import { locations } from '../../routes/locations';
import { ConfigKey } from '../../types/appConfig';
import { UpdatePortfolioRequestFile } from '../../types/portfolio';
import {
  UpdateSubscribedPortfolioRequestFile,
  UpdateSubscribedPortfolioRequestAssociatedIndexFund,
} from '../../types/subscribedPortfolio';
import { round2 } from '../../utils/number';
import { getAssetChartItems, getAssetChartItemsByGics } from '../../utils/portfolio';
import AssetDoughnutChart from '../Portfolio/AssetDoughnutChart';
import { FakeButtonLink } from '../../components/FakeLink';
import { Changes } from '../../components/Changes';
import { productTypeSelector } from '../../redux/modules/productType/product-type.selectors';
import { fetchProductTypes } from '../../redux/modules/productType/product-type.actions';

const marksPercentage = [
  { value: 0, label: '0%' },
  { value: 20, label: '20%' },
  { value: 50, label: '50%' },
  { value: 80, label: '80%' },
  { value: 100, label: '100%' },
];
const marksSharpRatio = [
  { value: 0, label: '0' },
  { value: 0.1, label: '0.1' },
  { value: 0.2, label: '0.2' },
  { value: 0.3, label: '0.3' },
  { value: 0.4, label: '0.4' },
  { value: 0.5, label: '0.5' },
  { value: 0.6, label: '0.6' },
  { value: 0.7, label: '0.7' },
  { value: 0.8, label: '0.8' },
  { value: 0.9, label: '0.9' },
  { value: 1, label: '1' },
];

const sectionCardSx = {
  borderRadius: 2,
  boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)',
  border: '1px solid',
  borderColor: 'divider',
};

const sectionAvatarSx = {
  bgcolor: 'rgba(25,118,210,0.1)',
  color: 'primary.main',
  width: 40,
  height: 40,
  borderRadius: 1.5,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  mr: 1,
};

const sectionHeaderSx = {
  pb: 1,
};

const accordionSx = {
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: 'none',
  '&:before': { display: 'none' },
  '&:not(:last-of-type)': { mb: 1.5 },
  '&.Mui-expanded': { mt: 0 },
};

const polishedFormContentSx = {
  p: 3,
  '& .form-block': {
    p: 2,
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'rgba(148, 163, 184, 0.06)',
  },
  '& .MuiTextField-root, & .MuiFormControl-root': {
    '& .MuiInputBase-input': {
      fontSize: 14,
    },
    '& .MuiInputLabel-root': {
      fontWeight: 600,
      color: 'text.secondary',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: 'primary.main',
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: 1.75,
      minHeight: 44,
      bgcolor: 'rgba(255,255,255,0.95)',
      transition: 'all .18s ease',
      '& fieldset': {
        borderColor: 'rgba(15, 23, 42, 0.12)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(25, 118, 210, 0.45)',
      },
      '&.Mui-focused': {
        bgcolor: 'background.paper',
        boxShadow: '0 0 0 3px rgba(25,118,210,0.12)',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'primary.main',
        borderWidth: '1px',
      },
      '&.Mui-disabled': {
        bgcolor: 'rgba(248,250,252,0.92)',
      },
      '&.Mui-disabled fieldset': {
        borderColor: 'rgba(15, 23, 42, 0.1)',
      },
    },
    '& .MuiOutlinedInput-input.Mui-disabled': {
      WebkitTextFillColor: 'rgba(15,23,42,0.72)',
    },
    '& .MuiSelect-select': {
      fontWeight: 500,
    },
    '& .MuiOutlinedInput-multiline': {
      alignItems: 'flex-start',
      py: 0.5,
    },
    '& .MuiFormHelperText-root': {
      mx: 0,
      mt: 0.75,
    },
  },
};

export function SubscribedPortfolio() {
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const navigate = useNavigate();
  const [chartData, setChartData] = useState([{ label: '', value: 0 }]);
  const [formSchema, setFormSchema] = useState(getSubscribedPortfolioFormSchema(0));
  const {
    fetchStatus,
    editSubscribedPortfolio,
    saveStatus,
    updateStatus,
    subscribedPortfolioStatusOptions,
    fetchSubscribedPortfolioStatusOptionsStatus,
    error: subscribedPortfolioError,
  } = useSelector(subscribedPortfolioSelector);
  const { portfolioFileTypeOptions, fetchPortfolioFileTypeOptionsStatus } = useSelector(portfolioSelector);
  const { fetchStatus: assetFetchStatus, assets, error: assetError } = useSelector(assetSelector);
  const {
    fetchStatus: indexFundFetchStatus,
    indexFunds,
    error: indexFundError,
    fetchStatus: gicsFetchStatus,
    gicsOptions,
  } = useSelector(indexFundSelector);
  const { fetchStatus: currencyFetchStatus, currencys, error: currencyError } = useSelector(currencySelector);
  const { fetchStatus: benchmarkFetchStatus, benchmarks, error: benchmarkError } = useSelector(benchmarkSelector);
  const { fetchStatus: appConfigFetchStatus, appConfigs, error: appConfigError } = useSelector(appConfigSelector);
  const {
    fetchStatus: productTypeFetchStatus,
    productTypes,
    error: productTypeError,
  } = useSelector(productTypeSelector);
  const {
    fetchStatus: fundManagerFetchStatus,
    fundManagers,
    error: fundManagerError,
  } = useSelector(fundManagerSelector);
  const { enqueueSnackbar } = useSnackbar();
  const isLoading =
    fetchStatus === fetchStatuses.pending ||
    assetFetchStatus === fetchStatuses.pending ||
    gicsFetchStatus === fetchStatuses.pending ||
    currencyFetchStatus === fetchStatuses.pending ||
    fundManagerFetchStatus === fetchStatuses.pending ||
    indexFundFetchStatus === fetchStatuses.pending ||
    benchmarkFetchStatus === fetchStatuses.pending ||
    appConfigFetchStatus === fetchStatuses.pending ||
    fetchPortfolioFileTypeOptionsStatus === fetchStatuses.pending ||
    fetchSubscribedPortfolioStatusOptionsStatus === fetchStatuses.pending ||
    productTypeFetchStatus === fetchStatuses.pending;
  const isSaveLoading = isLoading || saveStatus === fetchStatuses.pending;
  const error =
    subscribedPortfolioError ||
    currencyError ||
    fundManagerError ||
    assetError ||
    indexFundError ||
    benchmarkError ||
    appConfigError ||
    productTypeError;
  const formik = useFormik({
    initialValues: {
      [subscribedPortfolioFormFields.nameEn]: '',
      [subscribedPortfolioFormFields.nameAr]: '',
      [subscribedPortfolioFormFields.descriptionEn]: '',
      [subscribedPortfolioFormFields.descriptionAr]: '',
      [subscribedPortfolioFormFields.objectiveEn]: '',
      [subscribedPortfolioFormFields.objectiveAr]: '',
      [subscribedPortfolioFormFields.shariaaComplaint]: false,
      [subscribedPortfolioFormFields.status]: 0,
      [subscribedPortfolioFormFields.currency]: 0,
      [subscribedPortfolioFormFields.manager]: 0,
      [subscribedPortfolioFormFields.expectedMinimumReturn]: 0,
      [subscribedPortfolioFormFields.targetExpectedReturn]: 0,
      [subscribedPortfolioFormFields.expectedMaximumReturn]: 0,
      [subscribedPortfolioFormFields.displayByGics]: false,
      [subscribedPortfolioFormFields.targetExpectedRisk]: 0,
      [subscribedPortfolioFormFields.benchmark]: 0,
      [subscribedPortfolioFormFields.productType]: 0,
      [subscribedPortfolioFormFields.sharpRatio]: 0,
      [subscribedPortfolioFormFields.files]: [] as UpdateSubscribedPortfolioRequestFile[],
      [subscribedPortfolioFormFields.associatedIndexFunds]: [] as UpdateSubscribedPortfolioRequestAssociatedIndexFund[],
    },
    validationSchema: formSchema,
    onSubmit: (formValues) => {
      formValues.associatedIndexFunds = formValues.associatedIndexFunds.map((aif) => ({ ...aif, weight: +aif.weight }));

      if (Number(id)) {
        dispatch(updateSubscribedPortfolio({ id: Number(id), ...formValues }));
      } else {
        dispatch(saveSubscribedPortfolio(formValues));
      }
    },
  });
  const { handleSubmit, values, handleChange, touched, errors, setValues } = formik;
  const cashAllocationPercentage = Number(
    appConfigs.find((i) => i.key === ConfigKey.CACHE_ALLOCATION_PERCENTAGE)?.value || 0,
  );

  useEffect(() => {
    const chartItems = values[subscribedPortfolioFormFields.displayByGics]
      ? getAssetChartItemsByGics(indexFunds, values[portfolioFormFields.associatedIndexFunds], cashAllocationPercentage)
      : getAssetChartItems(indexFunds, values[portfolioFormFields.associatedIndexFunds], cashAllocationPercentage);
    setChartData(chartItems);
  }, [values[subscribedPortfolioFormFields.displayByGics]]);

  useEffect(() => {
    setFormSchema(getSubscribedPortfolioFormSchema(cashAllocationPercentage));
  }, [cashAllocationPercentage]);

  useEffect(() => {
    dispatch(fetchFundManagers({ limit: 100, offset: 0 }));
    dispatch(fetchCurrencys({ limit: 100, offset: 0 }));
    dispatch(fetchAssets({ limit: 100, offset: 0 }));
    dispatch(fetchGicsOptions());
    dispatch(fetchIndexFunds({ limit: 100, offset: 0 }));
    dispatch(fetchBenchmarks({ limit: 100, offset: 0 }));
    dispatch(fetchAppConfigs());
    dispatch(fetchSubscribedPortfolioStatusOptions());
    dispatch(fetchPortfolioFileTypeOptions());
    dispatch(fetchProductTypes({ limit: 100, offset: 0 }));

    return () => {
      dispatch(resetEditSubscribedPortfolio());
    };
  }, []);

  useEffect(() => {
    if (saveStatus === fetchStatuses.success) {
      dispatch(fetchSubscribedPortfolio(Number(id)));
      enqueueSnackbar('Subscribed IndexFund saved!', { variant: 'success' });
    }
    if (updateStatus === fetchStatuses.success) {
      dispatch(fetchSubscribedPortfolio(Number(id)));
      enqueueSnackbar('Subscribed IndexFund updated!', { variant: 'success' });
    }
  }, [saveStatus, updateStatus]);

  useEffect(() => {
    if (Number(id)) {
      dispatch(fetchSubscribedPortfolio(Number(id)));
    }
  }, [id]);

  useEffect(() => {
    if (editSubscribedPortfolio?.id && editSubscribedPortfolio?.id !== Number(id)) {
      navigate(locations.subscribedPortfolio(editSubscribedPortfolio?.id));
      return;
    }

    if (editSubscribedPortfolio) {
      const associatedIndexFunds =
        editSubscribedPortfolio.associatedIndexFunds?.map((aif) => ({
          id: aif.id,
          indexFund: aif?.indexFund?.id,
          weight: aif.weight,
        })) || [];

      setValues({
        [subscribedPortfolioFormFields.nameEn]: editSubscribedPortfolio.nameEn,
        [subscribedPortfolioFormFields.nameAr]: editSubscribedPortfolio.nameAr,
        [subscribedPortfolioFormFields.descriptionEn]: editSubscribedPortfolio.descriptionEn,
        [subscribedPortfolioFormFields.descriptionAr]: editSubscribedPortfolio.descriptionAr,
        [subscribedPortfolioFormFields.objectiveEn]: editSubscribedPortfolio.objectiveEn,
        [subscribedPortfolioFormFields.objectiveAr]: editSubscribedPortfolio.objectiveAr,
        [subscribedPortfolioFormFields.shariaaComplaint]: editSubscribedPortfolio.shariaaComplaint,
        [subscribedPortfolioFormFields.status]: editSubscribedPortfolio.status?.id,
        [subscribedPortfolioFormFields.currency]: editSubscribedPortfolio.currency?.id,
        [subscribedPortfolioFormFields.manager]: editSubscribedPortfolio.manager?.id,
        [subscribedPortfolioFormFields.expectedMinimumReturn]: editSubscribedPortfolio.expectedMinimumReturn || 0,
        [subscribedPortfolioFormFields.targetExpectedReturn]: editSubscribedPortfolio.targetExpectedReturn || 0,
        [subscribedPortfolioFormFields.expectedMaximumReturn]: editSubscribedPortfolio.expectedMaximumReturn || 0,
        [subscribedPortfolioFormFields.targetExpectedRisk]: editSubscribedPortfolio.targetExpectedRisk || 0,
        [subscribedPortfolioFormFields.benchmark]: editSubscribedPortfolio.benchmark?.id || 0,
        [subscribedPortfolioFormFields.productType]: editSubscribedPortfolio.productType?.id || 0,
        [subscribedPortfolioFormFields.sharpRatio]: +editSubscribedPortfolio.sharpRatio || 0,
        [subscribedPortfolioFormFields.displayByGics]: editSubscribedPortfolio.displayByGics || false,
        [subscribedPortfolioFormFields.files]:
          editSubscribedPortfolio.files?.map((f) => ({
            id: f.id,
            fileId: f.fileId,
            portfolioFileType: f.portfolioFileType?.id,
            isPublic: f.isPublic,
          })) || [],
        [subscribedPortfolioFormFields.associatedIndexFunds]: associatedIndexFunds,
      });

      setChartData(getAssetChartItems(indexFunds, associatedIndexFunds, cashAllocationPercentage));
    }
  }, [editSubscribedPortfolio]);

  const getFilesKey = (index: number) => `file-${index}`;
  const getAssetKey = (index: number) => `asset-${index}`;
  const getAssociatedIndexFundsKey = (index: number) => `associated-index-funds-${index}`;
  const valueText = (value: number) => `${value}%`;

  const isEdit = Number(id) > 0;

  function getSavedIndexFund(index: number) {
    return editSubscribedPortfolio?.associatedIndexFunds?.[index]?.indexFund;
  }

  function getSelectedIndexFund(indexFundId: number) {
    return indexFunds.find((i) => i.id === indexFundId);
  }

  // In edit mode, form state keeps only indexFund id, so resolve the display name
  // from loaded options first, then from subscribedPortfolio payload as fallback.
  function getIndexFundName(indexFundId: number) {
    return (
      getSelectedIndexFund(indexFundId)?.nameEn ||
      editSubscribedPortfolio?.associatedIndexFunds?.find((item) => item.indexFund?.id === indexFundId)?.indexFund
        ?.nameEn ||
      `${indexFundId}`
    );
  }

  function getSelectedPortfolioFileType(fileTypeId: number) {
    return portfolioFileTypeOptions.find((i) => i.id === fileTypeId);
  }

  function getOriginalIndexFundWeight(indexFundId: number, index: number) {
    const originalIndexFund = editSubscribedPortfolio?.originalPortfolio?.associatedIndexFunds?.find(
      (item) => item.indexFund.id === indexFundId,
    );

    return originalIndexFund?.weight || editSubscribedPortfolio?.associatedIndexFunds?.[index]?.weight || 0;
  }

  const totalAllocation = round2(
    values[portfolioFormFields.associatedIndexFunds].reduce(
      (res, item) => res + +item.weight,
      cashAllocationPercentage,
    ),
  );

  const renderAssetsData = () => (
    <Stack spacing={1.5}>
      {values[portfolioFormFields.associatedIndexFunds].map((aif, index) => {
        const assetName = getSelectedIndexFund(aif.indexFund)?.asset?.nameEn || 'Unknown asset';
        const weight = round2(Number(aif.weight) || 0);

        return (
          <Paper key={getAssetKey(index)} variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="body2" fontWeight={600}>
                {assetName}
              </Typography>
              <Chip size="small" label={`${weight}%`} sx={{ fontWeight: 700 }} />
            </Stack>
            <LinearProgress
              variant="determinate"
              value={weight}
              sx={{
                height: 8,
                borderRadius: 999,
                backgroundColor: 'rgba(15,23,42,0.08)',
                '& .MuiLinearProgress-bar': { borderRadius: 999 },
              }}
            />
          </Paper>
        );
      })}
    </Stack>
  );

  const renderGicsData = () => {
    const gicsByWeight = values[portfolioFormFields.associatedIndexFunds].reduce(
      (res, aif) => {
        const indexFund = getSelectedIndexFund(aif.indexFund);

        if (indexFund) {
          for (const gics of indexFund.gics as any) {
            if (!gics?.gics?.id) continue;
            res[gics.gics.id] = (res[gics.gics.id] || 0) + (gics.weight * aif.weight) / 100;
          }
        }

        return res;
      },
      {} as Record<number, number>,
    );

    return (
      <Stack spacing={1.5}>
        {Object.keys(gicsByWeight).map((key, index) => {
          const gicsName = gicsOptions.find((g) => String(g.id) === key)?.nameEn || 'Unknown sector';
          const weight = round2(gicsByWeight[key] || 0);

          return (
            <Paper key={getAssetKey(index)} variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="body2" fontWeight={600}>
                  {gicsName}
                </Typography>
                <Chip size="small" label={`${weight}%`} sx={{ fontWeight: 700 }} />
              </Stack>
              <LinearProgress
                variant="determinate"
                value={weight}
                sx={{
                  height: 8,
                  borderRadius: 999,
                  backgroundColor: 'rgba(15,23,42,0.08)',
                  '& .MuiLinearProgress-bar': { borderRadius: 999 },
                }}
              />
            </Paper>
          );
        })}
      </Stack>
    );
  };

  const sliderField = (
    label: string,
    description: string,
    fieldName: keyof typeof values,
    fieldKey: string,
    min: number,
    max: number,
    step: number,
    marks: { value: number; label: string }[],
  ) => (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.75, height: '100%' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
        <Typography variant="subtitle2" color="text.primary" fontWeight={700}>
          {label}
        </Typography>
        <Chip
          size="small"
          label={max === 1 ? `${round2(Number(values[fieldName]))}` : `${round2(Number(values[fieldName]))}%`}
          color="primary"
          variant="outlined"
          sx={{ fontWeight: 700 }}
        />
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {description}
      </Typography>
      {isLoading ? (
        <Skeleton variant="rectangular" width="100%" height={64} sx={{ mt: 2 }} />
      ) : (
        <FormControl
          fullWidth
          margin="normal"
          error={(touched as any)[fieldKey] && !!(errors as any)[fieldKey]}
          sx={{ mt: 2, mb: 0 }}
        >
          <Slider
            aria-label={label}
            defaultValue={0}
            disabled={Number(id) > 0}
            id={fieldKey}
            name={fieldKey}
            value={values[fieldName] as number}
            valueLabelFormat={max === 1 ? (value) => `${value}` : valueText}
            onChange={handleChange}
            getAriaValueText={max === 1 ? (value) => `${value}` : valueText}
            min={min}
            max={max}
            step={step}
            valueLabelDisplay="off"
            marks={marks.filter(
              (_, index) => index === 0 || index === marks.length - 1 || index === Math.floor(marks.length / 2),
            )}
            sx={{
              px: 0.5,
              '& .MuiSlider-track': { border: 'none', height: 6 },
              '& .MuiSlider-rail': { height: 6, opacity: 0.25 },
              '& .MuiSlider-thumb': { width: 16, height: 16 },
              '& .MuiSlider-markLabel': { fontSize: 11, color: 'text.disabled' },
            }}
          />
          {(touched as any)[fieldKey] && <FormHelperText>{(errors as any)[fieldKey]}</FormHelperText>}
        </FormControl>
      )}
    </Paper>
  );

  const renderHeader = () => {
    const statusLabel = subscribedPortfolioStatusOptions.find(
      (s) => s.id === values[subscribedPortfolioFormFields.status],
    )?.name;

    return (
      <Card sx={{ ...sectionCardSx, mb: 3, overflow: 'visible' }}>
        <Box
          sx={{
            background: 'linear-gradient(135deg, rgba(25,118,210,0.06) 0%, rgba(25,118,210,0.02) 100%)',
            borderBottom: '1px solid',
            borderColor: 'divider',
            px: 3,
            py: 2.5,
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ md: 'center' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Typography variant="overline" color="text.secondary" letterSpacing={1}>
                  Subscribed Portfolio
                </Typography>
                {isEdit && (
                  <Chip
                    size="small"
                    label={`#${id}`}
                    sx={{ bgcolor: 'grey.100', color: 'text.secondary', fontWeight: 600, height: 20 }}
                  />
                )}
              </Stack>
              <Typography variant="h5" fontWeight={700} color="text.primary">
                {isLoading ? (
                  <Skeleton width={280} />
                ) : (
                  values[subscribedPortfolioFormFields.nameEn] || (isEdit ? 'Untitled Portfolio' : 'New Portfolio')
                )}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {statusLabel && (
                  <Chip size="small" label={statusLabel} color="primary" variant="outlined" sx={{ fontWeight: 500 }} />
                )}
                {values[subscribedPortfolioFormFields.shariaaComplaint] && (
                  <Chip
                    size="small"
                    label="Shariaa Compliant"
                    color="success"
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  />
                )}
                {editSubscribedPortfolio?.originalPortfolio && (
                  <Tooltip
                    title={`(${editSubscribedPortfolio.originalPortfolio.id}) ${editSubscribedPortfolio.originalPortfolio.nameEn}`}
                    followCursor
                  >
                    <FakeButtonLink
                      width="auto"
                      startIcon={<OpenInNewIcon fontSize="small" />}
                      href={locations.portfolio(editSubscribedPortfolio.originalPortfolio.id)}
                    >
                      {`Original #${editSubscribedPortfolio.originalPortfolio.id}`}
                    </FakeButtonLink>
                  </Tooltip>
                )}
                {editSubscribedPortfolio?.userId && (
                  <Tooltip title={`User ID: ${editSubscribedPortfolio.userId}`} followCursor>
                    <FakeButtonLink
                      width="auto"
                      startIcon={<PersonOutlineIcon fontSize="small" />}
                      href={locations.user(editSubscribedPortfolio.userId)}
                    >
                      {`User #${editSubscribedPortfolio.userId}`}
                    </FakeButtonLink>
                  </Tooltip>
                )}
              </Stack>
            </Stack>
            <LoadingButton
              type="submit"
              color="primary"
              variant="contained"
              size="large"
              endIcon={<SaveIcon />}
              loading={isSaveLoading}
              loadingPosition="end"
              sx={{ minWidth: 160, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              {editSubscribedPortfolio ? 'Save Changes' : 'Create Portfolio'}
            </LoadingButton>
          </Stack>
        </Box>
      </Card>
    );
  };

  const renderBasicInfoSection = () => (
    <Card sx={sectionCardSx}>
      <CardHeader
        avatar={
          <Box sx={sectionAvatarSx}>
            <InfoOutlinedIcon fontSize="small" />
          </Box>
        }
        title={
          <Typography variant="h6" fontWeight={600}>
            Basic Information
          </Typography>
        }
        subheader={
          <Typography variant="body2" color="text.secondary">
            Identity, descriptions, and classification
          </Typography>
        }
        sx={sectionHeaderSx}
      />
      <Divider />
      <CardContent sx={polishedFormContentSx}>
        <Paper className="form-block" variant="outlined">
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5, letterSpacing: 1 }}>
            Identity
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Portfolio naming and bilingual description
          </Typography>
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6}>
              {isLoading ? (
                <Skeleton variant="rectangular" width="100%" height={56} />
              ) : (
                <TextField
                  fullWidth
                  required
                  id="nameEn"
                  name={subscribedPortfolioFormFields.nameEn}
                  label="Name (English)"
                  value={values[subscribedPortfolioFormFields.nameEn]}
                  onChange={handleChange}
                  error={touched.nameEn && !!errors.nameEn}
                  helperText={touched.nameEn && errors.nameEn}
                  disabled={Number(id) > 0}
                  autoComplete="nameEn"
                />
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {isLoading ? (
                <Skeleton variant="rectangular" width="100%" height={56} />
              ) : (
                <TextField
                  fullWidth
                  required
                  id="nameAr"
                  name={subscribedPortfolioFormFields.nameAr}
                  label="Name (Arabic)"
                  value={values[subscribedPortfolioFormFields.nameAr]}
                  onChange={handleChange}
                  error={touched.nameAr && !!errors.nameAr}
                  helperText={touched.nameAr && errors.nameAr}
                  disabled={Number(id) > 0}
                  autoComplete="nameAr"
                  inputProps={{ dir: 'rtl' }}
                />
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {isLoading ? (
                <Skeleton variant="rectangular" width="100%" height={72} />
              ) : (
                <TextField
                  fullWidth
                  required
                  multiline
                  minRows={2}
                  id="descriptionEn"
                  name={subscribedPortfolioFormFields.descriptionEn}
                  label="Description (English)"
                  value={values[subscribedPortfolioFormFields.descriptionEn]}
                  onChange={handleChange}
                  error={touched.descriptionEn && !!errors.descriptionEn}
                  helperText={touched.descriptionEn && errors.descriptionEn}
                  disabled={Number(id) > 0}
                  autoComplete="descriptionEn"
                />
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {isLoading ? (
                <Skeleton variant="rectangular" width="100%" height={72} />
              ) : (
                <TextField
                  fullWidth
                  required
                  multiline
                  minRows={2}
                  id="descriptionAr"
                  name={subscribedPortfolioFormFields.descriptionAr}
                  label="Description (Arabic)"
                  value={values[subscribedPortfolioFormFields.descriptionAr]}
                  onChange={handleChange}
                  error={touched.descriptionAr && !!errors.descriptionAr}
                  helperText={touched.descriptionAr && errors.descriptionAr}
                  disabled={Number(id) > 0}
                  autoComplete="descriptionAr"
                  inputProps={{ dir: 'rtl' }}
                />
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {isLoading ? (
                <Skeleton variant="rectangular" width="100%" height={72} />
              ) : (
                <TextField
                  fullWidth
                  required
                  multiline
                  minRows={2}
                  id="objectiveEn"
                  name={subscribedPortfolioFormFields.objectiveEn}
                  label="Objective (English)"
                  value={values[subscribedPortfolioFormFields.objectiveEn]}
                  onChange={handleChange}
                  error={touched.objectiveEn && !!errors.objectiveEn}
                  helperText={touched.objectiveEn && errors.objectiveEn}
                  disabled={Number(id) > 0}
                  autoComplete="objectiveEn"
                />
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {isLoading ? (
                <Skeleton variant="rectangular" width="100%" height={72} />
              ) : (
                <TextField
                  fullWidth
                  required
                  multiline
                  minRows={2}
                  id="objectiveAr"
                  name={subscribedPortfolioFormFields.objectiveAr}
                  label="Objective (Arabic)"
                  value={values[subscribedPortfolioFormFields.objectiveAr]}
                  onChange={handleChange}
                  error={touched.objectiveAr && !!errors.objectiveAr}
                  helperText={touched.objectiveAr && errors.objectiveAr}
                  disabled={Number(id) > 0}
                  autoComplete="objectiveAr"
                  inputProps={{ dir: 'rtl' }}
                />
              )}
            </Grid>
          </Grid>
        </Paper>

        <Divider sx={{ my: 4 }} />

        <Paper className="form-block" variant="outlined">
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5, letterSpacing: 1 }}>
            Classification
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Operational settings and compliance controls
          </Typography>
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6} lg={4}>
              {isLoading ? (
                <Skeleton variant="rectangular" width="100%" height={56} />
              ) : (
                <FormControl fullWidth error={touched.status && !!errors.status}>
                  <InputLabel id="status-label" required>
                    Status
                  </InputLabel>
                  <Select
                    labelId="status-label"
                    name={subscribedPortfolioFormFields.status}
                    value={values[subscribedPortfolioFormFields.status] || ''}
                    label="Status"
                    onChange={handleChange}
                  >
                    {subscribedPortfolioStatusOptions.map((ps) => (
                      <MenuItem key={ps.id} value={ps.id}>
                        {ps.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.status && <FormHelperText>{errors.status}</FormHelperText>}
                </FormControl>
              )}
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              {isLoading ? (
                <Skeleton variant="rectangular" width="100%" height={56} />
              ) : (
                <FormControl fullWidth error={touched.currency && !!errors.currency}>
                  <InputLabel id="currency-label" required>
                    Currency
                  </InputLabel>
                  <Select
                    labelId="currency-label"
                    name={subscribedPortfolioFormFields.currency}
                    value={values[subscribedPortfolioFormFields.currency] || ''}
                    label="Currency"
                    onChange={handleChange}
                    defaultValue={0}
                    disabled={Number(id) > 0}
                  >
                    {currencys.map((ps) => (
                      <MenuItem key={ps.id} value={ps.id}>
                        {ps.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.currency && <FormHelperText>{errors.currency}</FormHelperText>}
                </FormControl>
              )}
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              {isLoading ? (
                <Skeleton variant="rectangular" width="100%" height={56} />
              ) : (
                <FormControl fullWidth error={touched.manager && !!errors.manager}>
                  <InputLabel id="manager-label" required>
                    Manager
                  </InputLabel>
                  <Select
                    labelId="manager-label"
                    name={portfolioFormFields.manager}
                    value={values[portfolioFormFields.manager] || ''}
                    label="Manager"
                    onChange={handleChange}
                    required
                    defaultValue={0}
                    disabled={Number(id) > 0}
                  >
                    {fundManagers.map((ps) => (
                      <MenuItem key={ps.id} value={ps.id}>
                        {ps.nameEn}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.manager && <FormHelperText>{errors.manager}</FormHelperText>}
                </FormControl>
              )}
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              {isLoading ? (
                <Skeleton variant="rectangular" width="100%" height={56} />
              ) : (
                <FormControl fullWidth error={touched.benchmark && !!errors.benchmark}>
                  <InputLabel id="benchmark-label" required>
                    Benchmark
                  </InputLabel>
                  <Select
                    labelId="benchmark-label"
                    name={subscribedPortfolioFormFields.benchmark}
                    value={values[subscribedPortfolioFormFields.benchmark] || ''}
                    label="Benchmark"
                    onChange={handleChange}
                    required
                    disabled
                    defaultValue={0}
                  >
                    {benchmarks.map((ps) => (
                      <MenuItem key={ps.id} value={ps.id}>
                        {ps.nameAr}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.benchmark && <FormHelperText>{errors.benchmark}</FormHelperText>}
                </FormControl>
              )}
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              {isLoading ? (
                <Skeleton variant="rectangular" width="100%" height={56} />
              ) : (
                <FormControl fullWidth error={touched.productType && !!errors.productType}>
                  <InputLabel id="product-type-label" required>
                    Product Type
                  </InputLabel>
                  <Select
                    labelId="product-type-label"
                    name={subscribedPortfolioFormFields.productType}
                    value={values[subscribedPortfolioFormFields.productType] || ''}
                    label="Product Type"
                    onChange={handleChange}
                    required
                    disabled
                    defaultValue={0}
                  >
                    {productTypes.map((ps) => (
                      <MenuItem key={ps.id} value={ps.id}>
                        {ps.nameAr}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.productType && <FormHelperText>{errors.productType}</FormHelperText>}
                </FormControl>
              )}
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              {isLoading ? (
                <Skeleton variant="rectangular" width="100%" height={56} />
              ) : (
                <Paper
                  variant="outlined"
                  sx={{
                    height: 56,
                    px: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderRadius: 1,
                  }}
                >
                  <Stack>
                    <Typography variant="body2" fontWeight={500}>
                      Shariaa Compliant
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Compliance flag
                    </Typography>
                  </Stack>
                  <Switch
                    name={subscribedPortfolioFormFields.shariaaComplaint}
                    checked={values[subscribedPortfolioFormFields.shariaaComplaint]}
                    onChange={handleChange}
                    disabled={Number(id) > 0}
                  />
                </Paper>
              )}
            </Grid>
          </Grid>
        </Paper>
      </CardContent>
    </Card>
  );

  const renderFilesSection = () => (
    <Card sx={sectionCardSx}>
      <CardHeader
        avatar={
          <Box sx={sectionAvatarSx}>
            <FolderOpenIcon fontSize="small" />
          </Box>
        }
        title={
          <Typography variant="h6" fontWeight={600}>
            Documents
          </Typography>
        }
        subheader={
          <Typography variant="body2" color="text.secondary">
            {values[subscribedPortfolioFormFields.files].length} file(s) attached
          </Typography>
        }
        sx={sectionHeaderSx}
      />
      <Divider />
      <CardContent sx={polishedFormContentSx}>
        <FormikProvider value={formik}>
          <FieldArray
            name="files"
            render={() => (
              <Grid container spacing={2}>
                {values[subscribedPortfolioFormFields.files].length === 0 ? (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        textAlign: 'center',
                        py: 4,
                        color: 'text.secondary',
                        border: '1px dashed',
                        borderColor: 'divider',
                        borderRadius: 1,
                      }}
                    >
                      <InsertDriveFileOutlinedIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
                      <Typography variant="body2">No documents attached</Typography>
                    </Box>
                  </Grid>
                ) : (
                  values[subscribedPortfolioFormFields.files].map((item, index) => {
                    const fileType = getSelectedPortfolioFileType(item.portfolioFileType);
                    const errorMsg = (errors.files?.[index] as FormikErrors<UpdatePortfolioRequestFile>)?.fileId;
                    return (
                      <Grid item xs={12} md={6} key={getFilesKey(index)}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            borderRadius: 1.5,
                            transition: 'border-color .15s',
                            '&:hover': { borderColor: 'primary.main' },
                          }}
                        >
                          <Avatar sx={{ bgcolor: 'primary.lighter', color: 'primary.main' }}>
                            <UploadFileButton selectedFile={item.fileId} />
                          </Avatar>
                          <Box flex={1} minWidth={0}>
                            <Typography variant="subtitle2" fontWeight={600} noWrap>
                              {fileType?.name || 'Untitled File'}
                            </Typography>
                            <Typography
                              variant="caption"
                              color={item.fileId ? 'text.secondary' : 'error.main'}
                              noWrap
                              sx={{ display: 'block' }}
                            >
                              {item.fileId || errorMsg || 'No file uploaded'}
                            </Typography>
                          </Box>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography variant="caption" color="text.secondary">
                              {item.isPublic ? 'Public' : 'Private'}
                            </Typography>
                            <Switch
                              edge="end"
                              size="small"
                              id={`files.${index}.isPublic`}
                              name={`files.${index}.isPublic`}
                              onChange={handleChange}
                              checked={item.isPublic}
                              disabled
                              inputProps={{ 'aria-labelledby': 'switch-file-public' }}
                            />
                          </Stack>
                        </Paper>
                      </Grid>
                    );
                  })
                )}
              </Grid>
            )}
          />
          {!!errors.files && typeof errors.files === 'string' && (
            <FormHelperText error sx={{ mt: 1 }}>
              {errors.files}
            </FormHelperText>
          )}
        </FormikProvider>
      </CardContent>
    </Card>
  );

  const renderTargetsSection = () => (
    <Card sx={sectionCardSx}>
      <CardHeader
        avatar={
          <Box sx={sectionAvatarSx}>
            <TrendingUpIcon fontSize="small" />
          </Box>
        }
        title={
          <Typography variant="h6" fontWeight={600}>
            Performance Targets
          </Typography>
        }
        subheader={
          <Typography variant="body2" color="text.secondary">
            Expected returns, risk, and Sharpe ratio
          </Typography>
        }
        sx={sectionHeaderSx}
      />
      <Divider />
      <CardContent sx={polishedFormContentSx}>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.75, mb: 2 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" color="text.secondary">
                Min Return
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {values[subscribedPortfolioFormFields.expectedMinimumReturn]}%
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" color="text.secondary">
                Target Return
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {values[subscribedPortfolioFormFields.targetExpectedReturn]}%
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" color="text.secondary">
                Risk
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {values[subscribedPortfolioFormFields.targetExpectedRisk]}%
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" color="text.secondary">
                Sharpe Ratio
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {round2(values[subscribedPortfolioFormFields.sharpRatio])}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
        <Grid container columnSpacing={3} rowSpacing={2}>
          <Grid item xs={12} md={6}>
            {sliderField(
              'Expected Minimum Return',
              'Floor estimate (%)',
              subscribedPortfolioFormFields.expectedMinimumReturn,
              subscribedPortfolioFormFields.expectedMinimumReturn,
              0,
              100,
              1,
              marksPercentage,
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {sliderField(
              'Target Expected Return',
              'Goal estimate (%)',
              subscribedPortfolioFormFields.targetExpectedReturn,
              subscribedPortfolioFormFields.targetExpectedReturn,
              0,
              100,
              1,
              marksPercentage,
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {sliderField(
              'Expected Maximum Return',
              'Ceiling estimate (%)',
              subscribedPortfolioFormFields.expectedMaximumReturn,
              subscribedPortfolioFormFields.expectedMaximumReturn,
              0,
              100,
              1,
              marksPercentage,
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {sliderField(
              'Target Expected Risk',
              'Volatility tolerance (%)',
              subscribedPortfolioFormFields.targetExpectedRisk,
              subscribedPortfolioFormFields.targetExpectedRisk,
              0,
              100,
              1,
              marksPercentage,
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {sliderField(
              'Sharpe Ratio',
              'Risk-adjusted return',
              subscribedPortfolioFormFields.sharpRatio,
              subscribedPortfolioFormFields.sharpRatio,
              0,
              1,
              0.1,
              marksSharpRatio,
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderAllocationSection = () => (
    <Card sx={sectionCardSx}>
      <CardHeader
        avatar={
          <Box sx={sectionAvatarSx}>
            <PieChartIcon fontSize="small" />
          </Box>
        }
        title={
          <Typography variant="h6" fontWeight={600}>
            Allocation Overview
          </Typography>
        }
        subheader={
          <Typography variant="body2" color="text.secondary">
            Portfolio allocation{' '}
            {values[subscribedPortfolioFormFields.displayByGics] ? 'by GICS sector' : 'by asset class'}
          </Typography>
        }
        action={
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mr: 2, mt: 1 }}>
            <Typography
              variant="caption"
              color={!values[subscribedPortfolioFormFields.displayByGics] ? 'primary.main' : 'text.secondary'}
              fontWeight={600}
            >
              Assets
            </Typography>
            <Switch
              size="small"
              name={subscribedPortfolioFormFields.displayByGics}
              checked={values[subscribedPortfolioFormFields.displayByGics]}
              onChange={handleChange}
            />
            <Typography
              variant="caption"
              color={values[subscribedPortfolioFormFields.displayByGics] ? 'primary.main' : 'text.secondary'}
              fontWeight={600}
            >
              GICS
            </Typography>
          </Stack>
        }
        sx={sectionHeaderSx}
      />
      <Divider />
      <CardContent sx={polishedFormContentSx}>
        <Grid container spacing={4}>
          <Grid item xs={12} lg={7}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5, mb: 2 }}>
              {isLoading ? (
                <Skeleton variant="rectangular" width="100%" height={56} />
              ) : (
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Cash Allocation
                    </Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {cashAllocationPercentage}%
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={values[subscribedPortfolioFormFields.displayByGics] ? 'Grouped by GICS' : 'Grouped by Asset'}
                    color="primary"
                    variant="outlined"
                  />
                </Stack>
              )}
            </Paper>
            <Box>{values[subscribedPortfolioFormFields.displayByGics] ? renderGicsData() : renderAssetsData()}</Box>
          </Grid>
          <Grid item xs={12} lg={5}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                minHeight: 360,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'radial-gradient(circle at 50% 0%, rgba(25,118,210,0.06), transparent 50%)',
              }}
            >
              <Box
                position="relative"
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="100%"
                minHeight={320}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                    zIndex: 2,
                    pointerEvents: 'none',
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 1 }}
                  >
                    Total
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    color={totalAllocation === 100 ? 'success.main' : 'warning.main'}
                  >
                    {totalAllocation}%
                  </Typography>
                </Box>
                <AssetDoughnutChart items={chartData} />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderAssociatedFundsSection = () => (
    <Card sx={sectionCardSx}>
      <CardHeader
        avatar={
          <Box sx={sectionAvatarSx}>
            <Inventory2OutlinedIcon fontSize="small" />
          </Box>
        }
        title={
          <Typography variant="h6" fontWeight={600}>
            Associated Funds / Index
          </Typography>
        }
        subheader={
          <Typography variant="body2" color="text.secondary">
            {values[subscribedPortfolioFormFields.associatedIndexFunds].length} fund(s) linked
          </Typography>
        }
        sx={sectionHeaderSx}
      />
      <Divider />
      <CardContent sx={polishedFormContentSx}>
        <FormikProvider value={formik}>
          <FieldArray
            name="associatedIndexFunds"
            render={(arrayHelpers) => (
              <Stack spacing={2}>
                {values[subscribedPortfolioFormFields.associatedIndexFunds] &&
                values[subscribedPortfolioFormFields.associatedIndexFunds].length > 0 ? (
                  values[subscribedPortfolioFormFields.associatedIndexFunds].map((item, index) => (
                    <Paper
                      key={getAssociatedIndexFundsKey(index)}
                      variant="outlined"
                      sx={{ p: 2, borderRadius: 1.5, '&:hover': { borderColor: 'primary.light' } }}
                    >
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={6} lg={4}>
                          {isLoading && <Skeleton variant="rectangular" width="100%" height={56} />}
                          {!isLoading && (
                            <Box display="flex" alignItems="center" gap={1}>
                              <Tooltip title={`Open Index Fund #${item.indexFund}`}>
                                <FakeButtonLink
                                  startIcon={<OpenInNewIcon fontSize="small" />}
                                  href={locations.indexFund(item.indexFund)}
                                >
                                  {item.indexFund}
                                </FakeButtonLink>
                              </Tooltip>
                              {isEdit && (
                                <TextField
                                  sx={{ flex: 1 }}
                                  fullWidth
                                  id={`associatedIndexFunds.${index}.fund`}
                                  label="Fund"
                                  type="text"
                                  value={getSavedIndexFund(index)?.nameEn || ''}
                                  disabled
                                />
                              )}
                              {!isEdit && (
                                <FormControl
                                  sx={{ flex: 1 }}
                                  error={
                                    touched.associatedIndexFunds?.[index]?.indexFund &&
                                    !!(
                                      errors.associatedIndexFunds?.[
                                        index
                                      ] as FormikErrors<UpdateSubscribedPortfolioRequestAssociatedIndexFund>
                                    )?.indexFund
                                  }
                                >
                                  <InputLabel id="fund-label" required>
                                    Fund
                                  </InputLabel>
                                  <Select
                                    labelId="fund-label"
                                    id={`associatedIndexFunds.${index}.fund`}
                                    name={`associatedIndexFunds.${index}.fund`}
                                    value={item.indexFund}
                                    label="Fund"
                                    onChange={handleChange}
                                  >
                                    {indexFunds.map((a) => (
                                      <MenuItem key={a.id} value={a.id}>
                                        {a.nameEn}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                  {touched.associatedIndexFunds?.[index]?.indexFund && (
                                    <FormHelperText>
                                      {
                                        (
                                          errors.associatedIndexFunds?.[
                                            index
                                          ] as FormikErrors<UpdateSubscribedPortfolioRequestAssociatedIndexFund>
                                        )?.indexFund
                                      }
                                    </FormHelperText>
                                  )}
                                </FormControl>
                              )}
                            </Box>
                          )}
                        </Grid>
                        <Grid item xs={12} md={6} lg={2}>
                          {isLoading && <Skeleton variant="rectangular" width="100%" height={56} />}
                          {!isLoading && isEdit && (
                            <TextField
                              fullWidth
                              id={`associatedIndexFunds.${index}.asset`}
                              label="Asset Class"
                              type="text"
                              value={getSavedIndexFund(index)?.asset?.nameEn || ''}
                              disabled
                            />
                          )}
                          {!isLoading && !isEdit && (
                            <FormControl
                              fullWidth
                              error={
                                touched.associatedIndexFunds?.[index]?.indexFund &&
                                !!(
                                  errors.associatedIndexFunds?.[
                                    index
                                  ] as FormikErrors<UpdateSubscribedPortfolioRequestAssociatedIndexFund>
                                )?.indexFund
                              }
                            >
                              <InputLabel id="asset-class-label" required>
                                Asset Class
                              </InputLabel>
                              <Select
                                labelId="asset-class-label"
                                id={`associatedIndexFunds.${index}.asset`}
                                name={`associatedIndexFunds.${index}.asset`}
                                value={getSelectedIndexFund(item.indexFund)?.asset?.id}
                                label="Asset Class"
                              >
                                {assets.map((a) => (
                                  <MenuItem key={a.id} value={a.id}>
                                    {a.nameEn}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        </Grid>
                        <Grid item xs={6} md={3} lg={2}>
                          {isLoading ? (
                            <Skeleton variant="rectangular" width="100%" height={56} />
                          ) : (
                            <TextField
                              fullWidth
                              id={`associatedIndexFunds.${index}.region`}
                              name={`associatedIndexFunds.${index}.region`}
                              label="Region"
                              type="text"
                              value={
                                (isEdit
                                  ? getSavedIndexFund(index)?.geographicalFocus?.nameEn
                                  : getSelectedIndexFund(item.indexFund)?.geographicalFocus?.nameEn) || ''
                              }
                              disabled
                            />
                          )}
                        </Grid>
                        <Grid item xs={6} md={3} lg={2}>
                          {isLoading ? (
                            <Skeleton variant="rectangular" width="100%" height={56} />
                          ) : (
                            <TextField
                              fullWidth
                              id={`associatedIndexFunds.${index}.strategy`}
                              name={`associatedIndexFunds.${index}.strategy`}
                              label="Strategy / Obj"
                              type="text"
                              value={
                                (isEdit
                                  ? getSavedIndexFund(index)?.assetClass?.nameEn
                                  : getSelectedIndexFund(item.indexFund)?.assetClass?.nameEn) || ''
                              }
                              disabled
                            />
                          )}
                        </Grid>
                        <Grid item xs={8} md={4} lg={1.5} display="flex" alignItems="center" gap={0.5}>
                          {isLoading ? (
                            <Skeleton variant="rectangular" width="100%" height={56} />
                          ) : (
                            <>
                              <Changes
                                prev={getOriginalIndexFundWeight(item.indexFund, index)}
                                next={editSubscribedPortfolio?.associatedIndexFunds?.[index]?.weight || 0}
                              />
                              <DecimalNumberField
                                fullWidth
                                id={`associatedIndexFunds.${index}.weight`}
                                name={`associatedIndexFunds.${index}.weight`}
                                value={item.weight || 0}
                                label="Weight %"
                                onChange={handleChange}
                                autoComplete="Weight"
                                error={
                                  touched.associatedIndexFunds?.[index]?.weight &&
                                  !!(
                                    errors.associatedIndexFunds?.[
                                      index
                                    ] as FormikErrors<UpdateSubscribedPortfolioRequestAssociatedIndexFund>
                                  )?.weight
                                }
                                helperText={
                                  touched.associatedIndexFunds?.[index]?.weight &&
                                  (
                                    errors.associatedIndexFunds?.[
                                      index
                                    ] as FormikErrors<UpdateSubscribedPortfolioRequestAssociatedIndexFund>
                                  )?.weight
                                }
                              />
                            </>
                          )}
                        </Grid>
                        <Grid
                          item
                          xs={4}
                          md={2}
                          lg="auto"
                          display="flex"
                          alignItems="center"
                          justifyContent="flex-end"
                          gap={0.5}
                        >
                          <Tooltip title="Remove">
                            <span>
                              <IconButton
                                color="error"
                                aria-label="Remove index fund"
                                size="small"
                                disabled={Number(id) > 0}
                                onClick={() => arrayHelpers.remove(index)}
                                sx={{ border: '1px solid', borderColor: 'divider' }}
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Add below">
                            <span>
                              <IconButton
                                color="primary"
                                aria-label="Add index fund"
                                size="small"
                                disabled={Number(id) > 0}
                                onClick={() =>
                                  arrayHelpers.insert(index + 1, {
                                    fund: '',
                                    asset: '',
                                    region: '',
                                    strategy: '',
                                    weight: 0,
                                  })
                                }
                                sx={{ border: '1px solid', borderColor: 'divider' }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))
                ) : (
                  <Box
                    sx={{
                      textAlign: 'center',
                      py: 5,
                      border: '1px dashed',
                      borderColor: 'divider',
                      borderRadius: 1.5,
                    }}
                  >
                    <Inventory2OutlinedIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      No index funds linked to this portfolio
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => arrayHelpers.push({ fund: '', asset: 0, region: '', strategy: '', weight: 0 })}
                    >
                      Add an Index Fund
                    </Button>
                  </Box>
                )}
              </Stack>
            )}
          />
        </FormikProvider>
      </CardContent>
    </Card>
  );

  const renderForm = () => (
    <form onSubmit={handleSubmit}>
      {renderHeader()}
      <Stack spacing={2}>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 2,
            background: 'linear-gradient(180deg, rgba(25,118,210,0.02) 0%, rgba(25,118,210,0.01) 100%)',
          }}
        >
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={3}>
              <Typography variant="caption" color="text.secondary">
                Status
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {subscribedPortfolioStatusOptions.find((s) => s.id === values[subscribedPortfolioFormFields.status])
                  ?.name || '-'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="caption" color="text.secondary">
                Currency
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {currencys.find((c) => c.id === values[subscribedPortfolioFormFields.currency])?.name || '-'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="caption" color="text.secondary">
                Total Allocation
              </Typography>
              <Typography
                variant="body2"
                fontWeight={700}
                color={totalAllocation === 100 ? 'success.main' : 'warning.main'}
              >
                {totalAllocation}%
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="caption" color="text.secondary">
                Linked Funds
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {values[subscribedPortfolioFormFields.associatedIndexFunds].length}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        <Accordion defaultExpanded sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack>
              <Typography variant="subtitle1" fontWeight={700}>
                Basic Information
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Identity and classification details
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>{renderBasicInfoSection()}</AccordionDetails>
        </Accordion>

        <Accordion sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack>
              <Typography variant="subtitle1" fontWeight={700}>
                Documents
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {values[subscribedPortfolioFormFields.files].length} attached file(s)
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>{renderFilesSection()}</AccordionDetails>
        </Accordion>

        <Accordion sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack>
              <Typography variant="subtitle1" fontWeight={700}>
                Performance Targets
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Returns, risk, and Sharpe ratio
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>{renderTargetsSection()}</AccordionDetails>
        </Accordion>

        <Accordion sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack>
              <Typography variant="subtitle1" fontWeight={700}>
                Allocation Overview
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Distribution by asset class or GICS
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>{renderAllocationSection()}</AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack>
              <Typography variant="subtitle1" fontWeight={700}>
                Associated Funds / Index
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {values[subscribedPortfolioFormFields.associatedIndexFunds].length} linked fund(s)
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>{renderAssociatedFundsSection()}</AccordionDetails>
        </Accordion>
      </Stack>
    </form>
  );

  return (
    <Container component="main" maxWidth="xl" sx={{ pb: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mt: 2, borderRadius: 1.5 }}>
          <pre style={{ margin: 0, fontFamily: 'inherit' }}>{error.message}</pre>
        </Alert>
      )}
      <Box mt={2}>{renderForm()}</Box>
    </Container>
  );
}
